import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@components/common/Button';
import TierBadge from '@components/common/TierBadge';
import DetailSection from '@components/common/DetailSection';
import { InfoGrid, InfoItem } from '@components/common/InfoGrid';
import { Assessment } from '@app-types';
import { tierToVariant } from '@utils/badgeMappers';
import { formatCalendarDate, formatDateOnly } from '@utils/dateUtils';
import styles from './AssessmentInfo.module.css';

interface AssessmentInfoProps {
  assessment: Assessment;
  canReassign?: boolean;
  onAssign?: () => void;
  showContractInfo?: boolean;
}

const AssessmentInfo: React.FC<AssessmentInfoProps> = ({
  assessment,
  canReassign = false,
  onAssign,
  showContractInfo = false
}) => {
  const navigate = useNavigate();

  const handleSupplierClick = () => {
    navigate(`/suppliers/${assessment.supplier_id}`);
  };

  const handleContractClick = () => {
    navigate(`/contracts/${assessment.contract_id}`);
  };

  return (
    <DetailSection title="Assessment Information">
      <InfoGrid>
        <InfoItem label="Assessment Name">{assessment.assessment_name}</InfoItem>

        <InfoItem label="Tier">
          <TierBadge variant={tierToVariant(assessment.tier_name)}>{assessment.tier_name}</TierBadge>
        </InfoItem>

        <InfoItem label="Risk Level">
          <div className={styles.riskValue}>{assessment.tier_risk_level}</div>
        </InfoItem>

        <InfoItem label="Scheduled Date">
          {formatDateOnly(assessment.scheduled_date, 'Not scheduled')}
        </InfoItem>

        <InfoItem label="Publish Date">
          {formatCalendarDate(assessment.publish_date, 'Not published')}
        </InfoItem>

        <InfoItem label="CM Final Response Date">
          {formatCalendarDate(assessment.cm_final_response_date)}
        </InfoItem>

        {showContractInfo && (
          <>
            <InfoItem label="Supplier">
              <button
                type="button"
                className={styles.clickableInfo}
                onClick={handleSupplierClick}
                title="View supplier details"
              >
                {assessment.supplier_name}
              </button>
            </InfoItem>

            <InfoItem label="Contract">
              <button
                type="button"
                className={styles.clickableInfo}
                onClick={handleContractClick}
                title="View contract details"
              >
                {assessment.contract_number} - {assessment.contract_name}
              </button>
            </InfoItem>
          </>
        )}

        <InfoItem label="Assessor">
          {assessment.assessor_name ? (
            <div className={styles.assessorAssigned}>
              <div className={styles.assessorDetails}>
                <div>{assessment.assessor_name}</div>
                {assessment.assessor_email && <div className={styles.assessorEmail}>{assessment.assessor_email}</div>}
              </div>
              {canReassign && onAssign && (
                <Button variant="secondary" size="sm" onClick={onAssign}>
                  Reassign
                </Button>
              )}
            </div>
          ) : (
            <div className={styles.unassigned}>
              <span>Unassigned</span>
              {canReassign && onAssign && (
                <Button variant="secondary" size="sm" onClick={onAssign}>
                  Assign
                </Button>
              )}
            </div>
          )}
        </InfoItem>
      </InfoGrid>
    </DetailSection>
  );
};

export default AssessmentInfo;
